import { Splide } from "@splidejs/splide";
import { CoastCamMediaFile, CoastCamMediaFileWithVirtualTime } from "./coastcam-mediafile-model";
export default class CoastCam {
    containerElem: HTMLElement;

    splide: Splide;
    splideElem: HTMLElement;

    isPlaying: boolean;
    playInterval: ReturnType<typeof setInterval>;

    batchSize = 30;
    totalImages = 1440; // 24(hr) * 60(min)
    imageIndexes: number[];
    imageIndexesByTime: number[]
    images: CoastCamMediaFile[];
    imagesClone: CoastCamMediaFileWithVirtualTime[];
    imageURLPrefix = "";

    playButton: HTMLButtonElement;
    goButton: HTMLButtonElement;
    timeInput: HTMLInputElement;
    slider: HTMLInputElement;

    selectedTime: string;

    currentDate: Date;
    currentTimeStr: string;
    currentTimeNum: number;

    constructor(containerId: string, locationFolder: string) {
        this.containerElem = document.getElementById(containerId);
        this.imageURLPrefix = locationFolder
        this.currentDate = new Date(); 
        this.currentTimeStr = this.currentDate.toTimeString().slice(0, 5).replace(":", ""); // "HHmm"
        this.currentTimeNum = parseInt(this.currentTimeStr);
        this.init();
    }
    async init() {
        this.initElements();
        this.imageIndexes = await this.initIndex();
        this.initSplide();
        this.loadImages();
        this.initSlider();
        this.attachEventListeners();
    }

    private initElements() {
        this.splideElem = this.containerElem.querySelector('#image-player .splide');
        this.timeInput = this.containerElem.querySelector('#timeInput');
        const padded = this.currentTimeNum.toString().padStart(4, '0');
        this.timeInput.value = `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
        this.selectedTime = this.timeInput.value;
        this.goButton = this.containerElem.querySelector('.go-button .button');
        this.playButton = this.containerElem.querySelector('.play-button');
        this.slider = this.containerElem.querySelector('#rangeSlider');
    }

    /**
     * Initialise a timeline index for the last 24 hours based on current time
     * @method initIndex
     * @return indices
     */
    private async initIndex() {
        await this.fetchImages(this.imageURLPrefix);

        if (this.images.length === 0) {
            console.warn("No images found, skipping index generation.");
            this.imageIndexes = [];
            this.totalImages = 0;
            return [];
        }

        const todayStr = new Date().toDateString();
        const isTodayDate = (iso: string) =>
            new Date(iso).toDateString() === todayStr;
        const fallbackUrl = "/assets/media/coastcam_outage.png";

        let imagesClone: CoastCamMediaFileWithVirtualTime[] = [];
        const virtualTimeSet = new Set<number>();

        for (const img of this.images) {
            // derive HHmm key
            let timeKey: number;
            if (img.fileUrl.includes("live.jpg") && img.createdTime) {
                timeKey = this.convertToVirtualTime(img.createdTime);
                if (timeKey === -1) continue;
            } else {
                timeKey = Number(img.fileName);
                if (isNaN(timeKey)) {
                    console.warn(`Skipped non-timestamped image: ${img.fileName}`);
                    continue;
                }
            }

            // remove any existing entry for this minute
            imagesClone = imagesClone.filter(e => e.virtualTime !== timeKey);

            // ─── ONLY FALLBACK FOR “TODAY” SLOTS ──────────────────────────────────
            // determine if this minute is “today” (<= currentTimeNum)
            const isTodaySlot = timeKey <= this.currentTimeNum;
            let fileUrl = img.fileUrl;
            if (
                isTodaySlot &&                          // only care about today’s minutes
                !img.fileUrl.includes("live.jpg") &&    // but never override live.jpg
                (!img.createdTime || !isTodayDate(img.createdTime))
            ) {
                // if it’s a today-slot and not actually from today => fallback
                fileUrl = fallbackUrl;
            }

            imagesClone.push({
                fileName: img.fileName,
                createdTime: img.createdTime,
                fileUrl,      // real or fallback
                imageAltText: img.imageAltText,
                virtualTime: timeKey,
                isLive: img.fileUrl.includes("live.jpg")
            });

            virtualTimeSet.add(timeKey);

            // live.jpg still sets the “now” marker (but only if truly from today)
            if (
                img.fileUrl.includes("live.jpg") &&
                img.createdTime &&
                isTodayDate(img.createdTime)
            ) {
                this.currentTimeNum = timeKey;
            }
        }

        // ─── unchanged: extract unique HHmm slots & sort for playback ─────────
        const uniqueTimes = Array.from(new Set(
            imagesClone.map(img => img.virtualTime!).filter((t): t is number => t != null)
        ));
        this.imageIndexesByTime = uniqueTimes.sort((a, b) => a - b);

        this.imageIndexes = this.relativeTimeSort(
            this.imageIndexesByTime,
            this.currentTimeNum
        ).reverse();

        // ─── update state ────────────────────────────────────────────────────────
        this.imagesClone = imagesClone;
        this.totalImages = this.imageIndexes.length;

        return this.imageIndexes;
    }

    private getMinutesSinceMidnight(time: number): number {
        const hours = Math.floor(time / 100);
        const minutes = time % 100;
        return hours * 60 + minutes;
    }

    private relativeTimeSort(timestamps: number[], currentTime: number): number[] {
        const nowMinutes = this.getMinutesSinceMidnight(currentTime);
        return timestamps.sort((a, b) => {
            const diffA = (1440 + nowMinutes - this.getMinutesSinceMidnight(a)) % 1440;
            const diffB = (1440 + nowMinutes - this.getMinutesSinceMidnight(b)) % 1440;
            return diffA - diffB; // closest to now comes first 
        });
    }


    private initSplide() {
        this.splide = new Splide(this.splideElem, {
            type: 'fade',
            loop: true,
            lazyLoad: 'nearby',
            drag: true,
            pagination: false,
            arrows: true
        })
        this.splide.mount();

        this.splide.on('move', (index) => {
            this.updateTimeInput(index)
            this.updateSlider(index);
        })

    }
    private initSlider() {
        let isDragging = false;

        this.slider.min = "0";
        this.slider.max = (this.imageIndexes.length - 1).toString();
        this.slider.value = (this.imageIndexes.length - 1).toString(); // Start at latest image (index 0)

        const updateIndex = () => {
            const index = parseInt(this.slider.value, 10);
            const time = this.imageIndexes[index].toString();
            this.goToTime(time.slice(0, 2) + ':' + time.slice(2, 4));
        };
        this.slider.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key == "ArrowRight" || e.key == "ArrowLeft") {
                updateIndex();
            }
        })
        this.slider.addEventListener('mousedown', () => {
            isDragging = true;
        });

        this.slider.addEventListener('mouseup', () => {
            if (isDragging) {
                updateIndex();
                isDragging = false;
            }
        });

        // For touch devices
        this.slider.addEventListener('touchstart', () => {
            isDragging = true;
        });

        this.slider.addEventListener('touchend', () => {
            if (isDragging) {
                updateIndex();
                isDragging = false;
            }
        });
    }

    private loadImages() {
        const imagesList = this.splideElem.querySelector('.splide__list');
        if (!imagesList) throw new Error('Could not find .splide__list');

        imagesList.innerHTML = ''; 
        if (this.imageIndexes.length === 0) {
            imagesList.innerHTML = '<li>No images available</li>';
            return;
        }

        const lookup = new Map(this.imagesClone.map(img => [img.virtualTime, img]));
        this.imageIndexes.forEach(index => {
            const imageObj = lookup.get(index);

            if (imageObj) {
                const listItem = document.createElement('li');
                listItem.classList.add('splide__slide');
                listItem.innerHTML = `
            <img data-splide-lazy="${imageObj.fileUrl}" 
                 alt="${imageObj.imageAltText || imageObj.fileName}" 
                 ${imageObj.isLive ? 'data-live="true"' : ''}>
        `;
                imagesList.appendChild(listItem);
            }
        });

        if (this.imageIndexes.length) {
            this.splide.refresh();
            this.splide.go(this.imageIndexes.length - 1);
        }
    }


    private attachEventListeners() {
        this.goButton.addEventListener('click', () => {
            this.goToTime(this.selectedTime);

        })
        this.timeInput.addEventListener('input', (e: Event) => {
            const input = e.target as HTMLInputElement;
            let value = input.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters

            if (value.length > 2) {
                value = value.slice(0, 2) + ':' + value.slice(2, 4);
            }
            input.value = value;
            this.selectedTime = value;
        })
        this.timeInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                let val = this.selectedTime.replace(":", "");
                if (parseInt(val) >= 2400) {
                    val = String("000" + (parseInt(val) - 2400)).slice(-4); // Convert the difference to 4 digit string
                    val = val.slice(0, 2) + ':' + val.slice(2, 4);
                    this.timeInput.value = val;
                    this.selectedTime = val;
                }
                this.goToTime(this.selectedTime);
            }
        })

        this.playButton.addEventListener('click', () => {
            const playIcon = this.playButton.querySelector('.dot-icon');
            if (!this.isPlaying) {
                this.playImages();
                this.playButton.setAttribute('aria-pressed', 'true');
                this.playButton.setAttribute('aria-label', 'play');
                playIcon.classList.add('dot-other-pause');
                playIcon.classList.remove('dot-other-play');
            } else {
                this.stopPlaying();
                this.playButton.setAttribute('aria-pressed', 'true');
                this.playButton.setAttribute('aria-label', 'pause');
                playIcon.classList.add('dot-other-play');
                playIcon.classList.remove('dot-other-pause');
            }
        })
    }

    private goToTime(time: string) {
        const numericTime = parseInt(time.replace(':', ''), 10);

        let idx = this.imageIndexesByTime.findIndex(t => t === numericTime);
        if (idx !== -1) {
            const realIndex = this.imageIndexes.indexOf(numericTime);
            this.splide.go(realIndex);
            return;
        }

        idx = this.imageIndexesByTime.findIndex(t => t > numericTime);

        if (idx === 0) {
            const realIndex = this.imageIndexes.indexOf(this.imageIndexesByTime[0]);
            this.splide.go(realIndex);
            return;
        }
        else if (idx === -1) {
            const realIndex = this.imageIndexes.indexOf(
                this.imageIndexesByTime[this.imageIndexesByTime.length - 1]
            );
            this.splide.go(realIndex);
            return;
        }
        else {
            const bigger = this.imageIndexesByTime[idx];
            const smaller = this.imageIndexesByTime[idx - 1];

            const diffBigger = bigger - numericTime;    // bigger is guaranteed > numericTime
            const diffSmaller = numericTime - smaller;  // smaller is guaranteed < numericTime

            if (diffBigger < diffSmaller) {
                // bigger is closer
                const realIndex = this.imageIndexes.indexOf(bigger);
                this.splide.go(realIndex);
            } else {
                // smaller is closer or it's a tie => pick smaller
                const realIndex = this.imageIndexes.indexOf(smaller);
                this.splide.go(realIndex);
            }
            return;
        }
    }

    private async playImages() {
        if (this.totalImages === 0) {
            console.warn("No images available to play.");
            return;
        }

        this.isPlaying = true;
        let currentIndex = this.splide.index;

        this.playInterval = setInterval(async () => {
            currentIndex++;
            if (currentIndex >= this.totalImages) {
                currentIndex = 0; // Restart from the first image
            }
            this.splide.go(currentIndex);
        }, 1000);
    }


    private stopPlaying() {
        clearInterval(this.playInterval);
        this.isPlaying = false;
    };

    private updateTimeInput(index: number) {
        if (!this.imageIndexes.length || index < 0 || index >= this.imageIndexes.length) {
            this.timeInput.value = "--:--"; // Show invalid time if no images
            return;
        }

        let time = this.imageIndexes[index].toString().padStart(4, '0');
        this.timeInput.value = `${time.slice(0, 2)}:${time.slice(2, 4)}`;
        this.selectedTime = this.timeInput.value;
    }

    private updateSlider(index: number) {
        this.slider.value = index.toString();
    }

    private async fetchImages(folderPath: string) {
        this.images = []; // Ensure images array is always defined
        try {
            const response = await fetch(`/api/CoastCam/images?folderPath=${encodeURIComponent(folderPath)}`);

            if (!response.ok) {
                throw new Error("Failed to fetch images");
            }

            this.images = await response.json();
        } catch (err) {
            console.error("Error fetching images:", err);
        }
    }

    private convertToVirtualTime(createdTime: string): number {
        const date = new Date(createdTime);
        if (isNaN(date.getTime())) return -1;
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return parseInt(hours + minutes, 10); // e.g., 1225
    }
}